import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AgnesCard,
  AgnesCardContent,
  AgnesCardDescription,
  AgnesCardTitle,
  AgnesPageHeader,
  AgnesStatCard,
} from '@agnes/ui';
import { ChevronRight, Home } from 'lucide-react';
import { homeShortcuts, HomeStat, homeStats } from '@/mocks/home';
import ServiceHome from '@/services/ServiceHome';
import { useAuthStore } from '@/stores/useAuthStore';
import { CardTitle } from '@/components/ui/SectionCard';
import { Stagger, StaggerItem } from '@/components/motion/Motion';

interface ResumoHome {
  empresas: number;
  reprocessamentosAtivos: number;
  pendencias: number;
  certidoesOkPercentual: number;
}

export default function PageHome() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const getUser = useAuthStore((s) => s.getUser);
  const [resumo, setResumo] = useState<ResumoHome | null>(null);

  useEffect(() => {
    if (!user.firstName) getUser();
  }, [user.firstName, getUser]);

  useEffect(() => {
    ServiceHome.resumo()
      .then((r) => setResumo(r.data))
      .catch(() => setResumo(null)); // mantém o fallback zerado se o BFF falhar
  }, []);

  // Mescla os valores do BFF no template de cards (título/subtítulo/cor do mock).
  const stats: HomeStat[] = resumo
    ? [
        {
          ...homeStats[0],
          value: resumo.empresas === 0 ? 'Nenhuma empresa atribuída' : resumo.empresas,
          subtitle: resumo.empresas === 0 ? undefined : homeStats[0].subtitle,
        },
        { ...homeStats[1], value: resumo.reprocessamentosAtivos },
        { ...homeStats[2], value: resumo.pendencias },
        { ...homeStats[3], value: `${resumo.certidoesOkPercentual}%` },
      ]
    : homeStats;

  const greeting = user.firstName ? `Bem-vindo, ${user.firstName}` : 'Bem-vindo';

  return (
    <>
      <AgnesPageHeader
        title={greeting}
        icon={<Home />}
        subtitle="Visão geral da plataforma Compliance."
      />

      <Stagger className="mt-2 grid grid-cols-2 gap-4 md:grid-cols-4">
        {stats.map((s) => (
          <StaggerItem key={s.title}>
            <AgnesStatCard title={s.title} value={s.value} subtitle={s.subtitle} color={s.color} />
          </StaggerItem>
        ))}
      </Stagger>

      <h2 className="mt-8 mb-3">
        <CardTitle>Atalhos</CardTitle>
      </h2>
      <Stagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" delay={0.1}>
        {homeShortcuts.map((c) => (
          <StaggerItem key={c.path}>
            <AgnesCard
              onClick={() => navigate(c.path)}
              className="pr-4 flex flex-row h-full items-center justify-between cursor-pointer transition-colors hover:border-agnes-primary"
            >
              <AgnesCardContent>
                <AgnesCardTitle>{c.label}</AgnesCardTitle>
                <AgnesCardDescription>{c.description}</AgnesCardDescription>
              </AgnesCardContent>
              <ChevronRight className="h-5 w-5 text-agnes-text-muted" />
            </AgnesCard>
          </StaggerItem>
        ))}
      </Stagger>
    </>
  );
}
